import type { Phase, Message, ComponentResponsesResponse } from '@/utils/graphql/generated/graphql';
import type {
  StatDeltas,
  ResponseAnalysis,
  MessageAnalysis,
  PhaseAnalysis,
  GameAnalysis,
} from './balancing.types';

function sumStats(stats: StatDeltas[]): StatDeltas {
  return stats.reduce(
    (acc, stat) => ({
      cash: acc.cash + stat.cash,
      investments: acc.investments + stat.investments,
      happiness: acc.happiness + stat.happiness,
    }),
    { cash: 0, investments: 0, happiness: 0 }
  );
}

function averageStats(stats: StatDeltas[]): StatDeltas {
  if (stats.length === 0) return { cash: 0, investments: 0, happiness: 0 };

  const sum = sumStats(stats);
  return {
    cash: sum.cash / stats.length,
    investments: sum.investments / stats.length,
    happiness: sum.happiness / stats.length,
  };
}

function minStats(stats: StatDeltas[]): StatDeltas {
  if (stats.length === 0) return { cash: 0, investments: 0, happiness: 0 };

  return {
    cash: Math.min(...stats.map((s) => s.cash)),
    investments: Math.min(...stats.map((s) => s.investments)),
    happiness: Math.min(...stats.map((s) => s.happiness)),
  };
}

function maxStats(stats: StatDeltas[]): StatDeltas {
  if (stats.length === 0) return { cash: 0, investments: 0, happiness: 0 };

  return {
    cash: Math.max(...stats.map((s) => s.cash)),
    investments: Math.max(...stats.map((s) => s.investments)),
    happiness: Math.max(...stats.map((s) => s.happiness)),
  };
}

function calculateVariance(stats: StatDeltas[]): number {
  if (stats.length === 0) return 0;

  const maxStat = maxStats(stats);
  const minStat = minStats(stats);

  const cashVariance = maxStat.cash - minStat.cash;
  const investmentsVariance = maxStat.investments - minStat.investments;
  const happinessVariance = maxStat.happiness - minStat.happiness;

  return Math.max(cashVariance, investmentsVariance, happinessVariance);
}

function checkDeadChoice(stats: StatDeltas[]): boolean {
  if (stats.length <= 1) return false;

  const first = stats[0];
  return stats.every(
    (s) =>
      s.cash === first.cash &&
      s.investments === first.investments &&
      s.happiness === first.happiness
  );
}

export function analyzeMessage(message: Message, index: number): MessageAnalysis {
  const responses = message.Response?.filter((r): r is ComponentResponsesResponse => r !== null) ?? [];

  if (responses.length === 0) {
    return {
      messageIndex: index,
      messageName: message.name ?? null,
      messageText: message.text,
      min: { cash: 0, investments: 0, happiness: 0 },
      max: { cash: 0, investments: 0, happiness: 0 },
      avg: { cash: 0, investments: 0, happiness: 0 },
      responseCount: 0,
      responses: [],
      hasDeadChoice: false,
      maxVariance: 0,
    };
  }

  const deltas: StatDeltas[] = responses.map((r) => ({
    cash: r.cash,
    investments: r.investments,
    happiness: r.happiness,
  }));

  const responseAnalyses: ResponseAnalysis[] = responses.map((r) => ({
    text: r.text,
    deltas: {
      cash: r.cash,
      investments: r.investments,
      happiness: r.happiness,
    },
    hasItem: r.item !== null && r.item !== undefined,
    hasAchievement: r.achievement !== null && r.achievement !== undefined,
  }));

  return {
    messageIndex: index,
    messageName: message.name ?? null,
    messageText: message.text,
    min: minStats(deltas),
    max: maxStats(deltas),
    avg: averageStats(deltas),
    responseCount: responses.length,
    responses: responseAnalyses,
    hasDeadChoice: checkDeadChoice(deltas),
    maxVariance: calculateVariance(deltas),
  };
}

export function analyzePhase(phase: Phase): PhaseAnalysis {
  const messages = phase.messages?.filter((m): m is Message => m !== null) ?? [];

  if (messages.length === 0) {
    return {
      phaseId: phase.documentId,
      phaseName: phase.name,
      messageCount: 0,
      min: { cash: 0, investments: 0, happiness: 0 },
      max: { cash: 0, investments: 0, happiness: 0 },
      avg: { cash: 0, investments: 0, happiness: 0 },
      messages: [],
    };
  }

  const messageAnalyses = messages.map((msg, idx) => analyzeMessage(msg, idx));

  return {
    phaseId: phase.documentId,
    phaseName: phase.name,
    messageCount: messages.length,
    min: sumStats(messageAnalyses.map((a) => a.min)),
    max: sumStats(messageAnalyses.map((a) => a.max)),
    avg: sumStats(messageAnalyses.map((a) => a.avg)),
    messages: messageAnalyses,
  };
}

export function analyzeGame(phases: Phase[]): GameAnalysis {
  const phaseAnalyses = phases.map(analyzePhase);

  return {
    phases: phaseAnalyses,
    total: {
      min: sumStats(phaseAnalyses.map((a) => a.min)),
      max: sumStats(phaseAnalyses.map((a) => a.max)),
      avg: sumStats(phaseAnalyses.map((a) => a.avg)),
    },
  };
}

export function getStatColor(value: number): string {
  if (value < 0) return 'text-red-600 bg-red-50';
  if (value < 5) return 'text-yellow-600 bg-yellow-50';
  return 'text-green-600 bg-green-50';
}

export function formatStatValue(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}`;
}
